import { defineVueAppElement } from './define-vue-app-element'
import VueApp from './VueApp.vue'
import { initInfoTooltips } from './utils/infoTooltips'
import { useRiskStore } from './stores/riskData'
import { restrictedPostcodeMessage, RESTRICTED_POSTCODE_MESSAGE } from './utils/postcodeRestriction'
import { cover_start_backdate } from './utils/coverStartRule'
import { isValidGbPhone } from './utils/gbPhone'
import { isValidErn } from './utils/ernNumber'
import { isSafeName, isSafeText } from './utils/safeText'
import { isValidUkPostcode } from './utils/ukPostcode'
import { ownerObject } from './utils/validatorOwner'
import { serverFieldErrors } from './composables/useQuoteErrors'
import { i18n } from './i18n'
import Vueform, { Validator } from '@vueform/vueform'
import en from '@vueform/vueform/locales/en'
import theme from '@vueform/vueform/dist/vueform'
import type { VueformConfig } from '@vueform/vueform'
import vueformCss from './assets/vueform.css?inline'
import multiselectCss from 'vue-multiselect/dist/vue-multiselect.min.css?inline'
import appCustomCss from './assets/custom/appCustom.scss?inline'
// @ts-ignore
import PostcodeLookupElement from '@/components/Form/PostCodeLookup/PostcodeLookupElement.vue'
// @ts-ignore
import FormSelectElement from '@/components/Form/Select/SelectElement.vue'
// @ts-ignore
import FormNavElement from '@/components/Form/Nav/FormNavElement.vue'
// @ts-ignore
import BCISCalculatorInfoElement from '@/components/Form/BCISCalculatorInfo/index.vue'
// @ts-ignore
import BrokerElement from '@/components/Form/Broker/index.vue'
// @ts-ignore
import MoneyElement from '@/components/Form/Money/MoneyElement.vue'
// @ts-ignore
import NumberStepperElement from '@/components/Form/NumberStepper/NumberStepperElement.vue'
// @ts-ignore
import ActivityFullListElement from '@/components/Form/ActivityFullList/ActivityFullListElement.vue'
import type { Plugin } from 'vue'
import router from './router'

// Idempotent: the entry module can be re-evaluated when a lazy schema chunk loads, which would
// otherwise inject a second (stale) copy of the styles that wins by source order.
const STYLE_ID = 'vue-app-styles'
const existingStyle = document.getElementById(STYLE_ID) as HTMLStyleElement | null
const style = existingStyle ?? document.createElement('style')
style.id = STYLE_ID
style.textContent = vueformCss + '\n' + multiselectCss + '\n' + appCustomCss
if (!existingStyle) document.head.appendChild(style)

class numeric_lte extends (Validator as any) {
    get msg() {
        return 'This value must not exceed the value of :other'
    }

    get messageParams() {
        return {
            attribute: this.attributeName,
            other: (this as any).other$?.genericName || (this as any).otherPath,
        }
    }

    get otherPath() {
        return this.attributes[0]
    }

    get other$() {
        return (this as any).form$.el$(this.otherPath)
    }

    init() {
        (this as any).watchOther()
    }

    check(value: any) {
        const otherValue = this.other$?.value
        if (value == null || otherValue == null) return true
        const num = parseInt(value)
        const otherNum = parseInt(otherValue)
        if (isNaN(num) || isNaN(otherNum)) return true
        return num <= otherNum
    }
}

class required_if extends (Validator as any) {
    get msg() {
        if (this.elementMessages && this.elementMessages[this.name]) {
            return undefined
        }
        return 'Reason for discount / loading is required when contents or buildings loading has a value'
    }

    init() {
        const otherFields = Object.keys(this.attributes)
            .filter((key) => !isNaN(Number(key)))
            .map((key) => this.attributes[key])
            .filter(Boolean)
            .map(path => path.startsWith('!') ? path.substring(1) : path)

        const getByPath = (source: any, path: string): any =>
            path.split('.').reduce((acc: any, key: string) => (acc == null ? acc : acc[key]), source)

        otherFields.forEach((path: string) => {
            (this as any).form$.$watch(
                () => getByPath((this as any).form$.data, path),
                () => {
                    if ((this as any).element$?.validated) {
                        (this as any).element$.validate()
                    }
                }
            )
        })
    }

    check(value: any) {
        const otherFields = Object.keys(this.attributes)
            .filter((key) => !isNaN(Number(key)))
            .map((key) => this.attributes[key])
            .filter(Boolean)

        const hasNegation = otherFields.some(path => path.startsWith('!'))

        if (hasNegation) {
            const fieldChecks = otherFields.map((path) => {
                const isNegated = path.startsWith('!')
                const fullPath = isNegated ? path.substring(1) : path
                const fieldEl = (this as any).form$?.el$(fullPath)
                const fieldValue = fieldEl?.value
                const hasValue = this.fieldHasValue(fieldValue)
                const result = isNegated ? !hasValue : hasValue
                return { path, fullPath, fieldValue, hasValue, isNegated, result }
            })

            const shouldRequire = fieldChecks.every(check => check.result)
            if (!shouldRequire) {
                return true
            }
        } else {
            const shouldRequire = otherFields.some((path) => {
                const fieldValue = (this as any).form$?.el$(path)?.value
                return this.fieldHasValue(fieldValue)
            })

            if (!shouldRequire) {
                return true
            }
        }

        if (typeof value === 'boolean' && !value) {
            return false
        }

        return value !== undefined && value !== null && String(value).trim() !== ''
    }

    fieldHasValue(fieldValue: any): boolean {
        if (fieldValue === undefined || fieldValue === null) {
            return false
        }
        if (Array.isArray(fieldValue)) {
            return fieldValue.length > 0
        }
        return String(fieldValue).trim() !== ''
    }
}

class postcode_requires_member extends (Validator as any) {
    get msg() {
        return RESTRICTED_POSTCODE_MESSAGE
    }

    check(value: any) {
        const member = useRiskStore().riskData?.MembershipId
        return restrictedPostcodeMessage(value, member) === null
    }
}

class activity_turnover_total extends (Validator as any) {
    get msg() {
        return 'The turnover split across your activities must add up to 100%.'
    }

    check() {
        const rows = (this as any).form$?.model?.ActivityTurnovers
        if (!Array.isArray(rows) || rows.length <= 1) return true
        const toNum = (v: any) => { const n = parseFloat(v); return Number.isNaN(n) ? 0 : n }
        const total = rows.reduce((sum: number, row: any) => sum + toNum(row?.TurnoverPercentage), 0)
        return Math.round(total) === 100
    }
}

// Custom rule: GB phone number. Mirrors the legacy forms' data-fv-phone___country="GB"
// Usage: rules: "nullable|gb_phone" or "required|gb_phone"
class gb_phone extends (Validator as any) {
    get msg() {
        return 'Please enter a valid UK phone number'
    }

    check(value: any) {
        return isValidGbPhone(value)
    }
}

// Custom rule: Employer Reference Number (ERN / Employer PAYE Reference), HMRC format 123/AB45678.
// Usage: rules: "required|ern"
class ern extends (Validator as any) {
    get msg() {
        return 'Please enter a valid Employer Reference Number, e.g. 123/AB45678'
    }

    check(value: any) {
        return isValidErn(value)
    }
}

// Custom rules: free-text allowlists that block coding/injection symbols.
// Usage: rules: "required|safe_name" (people/contact names) or "required|safe_text" (addresses, details).
class safe_name extends (Validator as any) {
    get msg() {
        return 'Please use only letters, spaces, hyphens (-) and apostrophes (’)'
    }

    check(value: any) {
        return isSafeName(value)
    }
}

class safe_text extends (Validator as any) {
    get msg() {
        return 'Please remove any special symbols such as < > = \\ or brackets'
    }

    check(value: any) {
        return isSafeText(value)
    }
}

// Custom rule: a premises address must have been captured (postcode searched + address selected, or
// entered manually)
class premises_address extends (Validator as any) {
    get msg() {
        return 'Please enter your postcode and select your premises address'
    }

    check() {
        const postcode = ownerObject(this)?.Postcode
        return typeof postcode === 'string' && postcode.trim() !== ''
    }
}

// Custom rule: UK-format postcode (also covers Guernsey/Jersey/Isle of Man, which use UK postcodes).
// Skipped when the address object's Country is "Other" (non-UK).
// Usage: rules: "required|uk_postcode"
class uk_postcode extends (Validator as any) {
    get msg() {
        return 'Please provide a valid postcode'
    }

    check(value: any) {
        if (ownerObject(this)?.Country === 'Other') return true
        return isValidUkPostcode(value)
    }
}

// Usage: rules: "required|no_errors:Email" — surfaces a server-side validation error for the mapped
// field until the user edits it. Args are the server ModelState key(s).
class no_errors extends (Validator as any) {
    get msg() {
        const key = this.serverKey
        return (key && serverFieldErrors[key]) || 'Please correct this field'
    }

    get serverKey() {
        const attrs = (this as any).attributes
        const keys: string[] = Array.isArray(attrs) ? attrs : attrs != null ? [attrs] : []
        return keys.find((k) => k in serverFieldErrors) ?? keys[0]
    }

    check() {
        const attrs = (this as any).attributes
        const keys: string[] = Array.isArray(attrs) ? attrs : attrs != null ? [attrs] : []
        // Valid unless the server flagged this field and the user hasn't edited it since.
        if (!keys.some((k) => k in serverFieldErrors)) return true
        const el$ = (this as any).element$ ?? (this as any).el$
        return el$?.dirty === true
    }
}

const enUK = {
    ...en,
    vueform: {
        ...en.vueform,
        dateFormats: {
            ...en.vueform.dateFormats,
            date: 'DD/MM/YYYY',
        },
    },
}

const cfg: VueformConfig = {
    theme,
    locales: { en: enUK },
    locale: 'en',
    // No floating placeholders anywhere (client request): the journey uses static labels above fields, so
    // placeholders show as plain empty-state hints that clear on input, rather than floating up as labels.
    floatPlaceholders: false,
    // Vueform sanitizes label HTML with DOMPurify, which drops `target` by default; allow it so
    // in-label links (e.g. Privacy Statement / terms & conditions) can open in a new tab.
    sanitizeOptions: { ADD_ATTR: ['target', 'rel'] },
    events: ['submit', 'change', 'blur', 'mount', 'unmount'],
    elements: [PostcodeLookupElement, FormSelectElement, FormNavElement, BCISCalculatorInfoElement, BrokerElement, MoneyElement, NumberStepperElement, ActivityFullListElement],
    rules: { numeric_lte, required_if, postcode_requires_member, activity_turnover_total, cover_start_backdate, gb_phone, premises_address, uk_postcode, ern, safe_name, safe_text, no_errors },
    addClasses: {
        Vueform: { form: 'is2-vue-form' },
        ObjectElement: { container: 'is2-vue-form-question-container' },
        GroupElement: { container: 'is2-vue-form-group-container' },
        ElementLabel: { container: 'is2-vue-form-section-label' },
        ElementLayout: { innerContainer: 'is2-vue-form-element-layout-inner-container' },
        RadiogroupElement: { wrapper: 'd-flex flex-wrap' },
        RadiogroupRadio: {
            container: 'p-15 align-items-center flex-row justify-content-center quote-radio-group',
            text: 'fw-600 pt-0',
            input: 'mt-0'
        },
        CheckboxgroupElement: { wrapper: 'd-flex flex-wrap' },
        CheckboxgroupCheckbox: {
            container: 'p-5 align-items-center flex-row justify-content-center quote-checkbox-group w-auto',
            input: 'my-0'
        },
        ElementInfo: { container: 'info-icon-container' }
    },
    classHelpers: true
}

defineVueAppElement('vue-app', VueApp, (app) => {
    useRiskStore()
    app.use(router)
    app.use(Vueform as unknown as Plugin, cfg)
    app.use(i18n)
})

// Touch tap-to-toggle for Vueform info tooltips (desktop keeps native hover).
initInfoTooltips()
